import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './vi-source-data.hbs'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import selects from 'scoreboard/components/selects'
import DateInput from 'scoreboard/components/date-input'
import SourceDataPanel from 'scoreboard/imports/source-data-panel'
import packageTemplate from 'scoreboard/imports/column-mapping-package.hbs'
import * as urlToLinks from '../../components/urls-to-links'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'

/**
 * This step 4 (for dbViews) of values import wizard is for identifying source data:
 *  - setting date reference (relative, specific date, value in column or row)
 *  - if existing values should be overwritten
 *  - mapping the columns
 */

const ViSourceData = Backbone.View.extend({

	tagName: 'form',

	className: 'import-source-data',

	events: {
		'change [name=dateReference]': 'onChangeDateReference',
		'change .use-row': 'highlightHeaderRow',
		'click .porter-package .delete': 'onDeleteMapping'
	},

	initialize: function (options) {
		this.options = options || {}
		const MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: () => _.isNil(this.test())
		})
	},

	render: function () {
		this.$el.empty().append(template(this.context()))
		this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		// date reference type is required
		// this would only be blank if an import somehow has a date reference
		// that is not valid for its type (#31159)
		this.markedField = new MarkedField({
			el: this.$('[name=dateReference]'),
			validator: requiredValidator
		}).render()

		this.dateInput = new DateInput({
			el: this.$('.specific-date'),
			initialValue: this.model.get('specificDate') || new Date()
		}).render()

		// "Dates are in a row" doesn't make sense for DB imports
		// or for initiative status imports
		if (this.model.isDatabase() || this.model.get('type') === 'initiative-status') {
			this.$('[name=dateReference] option[value=row]').remove()
		}

		// #31159 initiative imports don't use Relative date reference type
		if (this.model.get('type') === 'initiative-status') {
			this.$('[name=dateReference] option[value=relative]').remove()
		}

		selects(this.$('select'))
		formHelper.writeToDom(this.options.model, this.el)
		// #29366 "Overwrite Existing Values" checked by default
		if (this.model.isNew()) {
			this.$('input[name=overwriteExistingValues').prop('checked', true)
		}
		// if we have a dateColumn automapped, set dateReference to column
		let columnMappings = this.model.get('columnMappings')
		if (_.isEmpty(this.model.get('dateReference')) && !_.isEmpty(columnMappings.dateColumn)) {
			formHelper.writeToDom({dateReference: 'column'}, this.el)
		}
		this.toggleDateSelectors()
		this.renderBanner()
		this.renderSourceDataPanel()
		this.markRequiredOptions()
		this.highlightHeaderRow()
		this.formView.start()
		// #31642 display message if database is offline/unavailable
		let message = this.getDataSourceMetadataMessage()
		if (message) {
			this.formView.errorMessage(message)
		}
		return this
	},

	renderBanner: function() {
		// #30835 Add instructions to scorecard and initiative value imports
		let href = 'https://support.spiderstrategies.com/hc/en-us/articles/360041613491-Importing-KPI-and-Initiative-Values'
		let link = urlToLinks.createAnchor(href,
			'admin.import.sourceData.instructions.supportArticle')
		let html = i18n.get('admin.import.sourceData.instructions', link)
		this.$('.banner-description').html(html)
	},

	renderSourceDataPanel() {
		this.sourceDataPanel = new SourceDataPanel({
			model: this.model,
			formView: this.formView,
			validMappingOptions: this.validMappingOptions.bind(this),
			markRequiredOptions: this.markRequiredOptions.bind(this),
			headerIndex: this.model.headerRowIndex()
		})
		this.$('.preview-panel').append(this.sourceDataPanel.el)
		this.sourceDataPanel.render()
		// so we don't end up with more than one name option marked as required
		this.sourceDataPanel.on('deliver', () => this.markRequiredOptions())
	},

	getDataSourceMetadataMessage() {
		let messageCode = this.model.getDataSourceMetadata('messageCode')
		let message
		if (messageCode) {
			let label = _.get(this.model.attributes, 'connection.label')
			message = i18n.get(messageCode, label)
			message += ' ' + i18n.get('admin.import.sourceData.notAvailable')

		}
		return message
	},

	onChangeDateReference() {
		this.toggleDateSelectors()
		this.applyValidMappings()
		this.markRequiredOptions()
		this.highlightHeaderRow()
	},

	toggleDateSelectors: function () {
		let dateType = this.dateReference()
		let isRelative = dateType === 'relative'
		let isSpecific = dateType === 'specific'
		this.$('.period-offset').toggleClass('is-not-displayed', !isRelative)
		this.$('.specific-date').toggleClass('is-not-displayed', !isSpecific)
		// #60106 also toggle the specific date input's marked field so the
		// calendar icon is only showing when using a specific date
		this.dateInput.toggle(isSpecific)
	},

	/**
	 * Highlights the designated header row in blue.
	 * If this is a value import using "dates are in a row",
	 * or if a header row has been selected in the worksheet options step,
	 * this will be the first non-ignored row of data
	 */
	highlightHeaderRow() {
		this.$('.date-row').removeClass('date-row')
		let dataSourceMetadata = this.model.getDataSourceMetadata()
		let isDbImport = dataSourceMetadata.source === 'database'
		let headerIndex = this.model.headerRowIndex()
		let headerRow
		if (isDbImport) {
			// #21773 #32113 mark the first row as the header row for db imports
			headerRow = this.$('table tbody tr:first')
		} else {
			// for non-db imports
			if (!_.isNil(headerIndex)) {
				// #32110 if we have a headerIndex defined, we know our header row
				headerRow = this.$('table tbody').find('tr:not(.is-not-displayed)').eq(headerIndex)
			} else {
				// for date header rows, it will always be the first non-ignored row
				// whether the header row was selected or not
				if (this.dateReference() === 'row') {
					// find the first row that is being used, and mark it as the date row
					headerRow = this.$('tbody tr:not(.is-not-displayed, .is-ignored)').first()
				}
			}
		}
		// #32146 it's possible that an import might not have a header row
		if (!_.isNil(headerRow)) {
			headerRow.removeClass('is-ignored').addClass('date-row')
		}
	},

	/**
	 * Makes any necessary changes to the existing mappings to make sure only
	 * valid options are displayed and used
	 */
	applyValidMappings () {
		let validOptions = this.validMappingOptions()

		let currentOptionNodes = this.$('.porter-package').toArray()
		let currentOptionIds = _(currentOptionNodes).map(n => n.dataset.id).uniq().value()

		validOptions.forEach(option => {
			if (!_.includes(currentOptionIds, option.id)) {
				// If a valid option isn't currently displayed, add it to the
				// "available" pool
				this.sourceDataPanel.porter.add('available', $(packageTemplate(option))[0])
			}
		})

		currentOptionNodes.forEach(option => {
			if (!_.find(validOptions, valid => valid.id === option.dataset.id)) {
				// If an existing option is no longer valid, remove it
				this.sourceDataPanel.porter.delete(option)
			}
		})
	},

	context: function () {
		return {
			title: i18n.get('admin.import.sourceData')
		}
	},

	saveState: function() {
		let data = formHelper.readFromDom(this.el)

		data = _.pick(data, 'dateReference', 'periodOffset', 'specificDate', 'overwriteExistingValues')

		// remove extraneous values for the date selection type
		let dateReference = this.dateReference()
		if (dateReference === 'relative') {
			data = _.omit(data, 'specificDate')
		} else if (dateReference === 'specific') {
			data =  _.omit(data, 'periodOffset')
		} else if (dateReference === 'row' || dateReference === 'column') {
			data = _.omit(data, ['periodOffset', 'specificDate'])
		}

		let columnMappings = {}
		let nameColumns = []
		columnMappings.valueColumns = []
		columnMappings.thresholdColumns = [] // aka 'thresholds' in AbstractTabularImportForm
		columnMappings.dateColumn = null
		columnMappings.noteColumn = null
		columnMappings.budgetSpentColumn = null
		columnMappings.percentCompleteColumn = null

		let headerVals = this.sourceDataPanel.porter.val()

		_.each(headerVals, (typePackages, columnName) => {
			if (columnName === 'available') {
				return
			}

			// only 1 package allowed in each column
			let typePackage = typePackages[0]
			if (typePackage) {
				let colType = typePackage.id
				switch (colType) {
					case 'name':
						// Record the name mapping's order, so we can resort them
						// and save them in the proper order
						nameColumns.push({
							name: columnName,
							order: parseInt(typePackage.node.dataset.order)
						})
						break
					case 'threshold':
						columnMappings.thresholdColumns.push(columnName)
						break
					case 'value':
						columnMappings.valueColumns.push(columnName)
						break
					case 'date':
						columnMappings.dateColumn = columnName
						break
					case 'note':
						columnMappings.noteColumn = columnName
						break
					case 'orgLabel':
						columnMappings.orgLabelColumn = columnName
						break
					case 'budgetSpent':
						columnMappings.budgetSpentColumn = columnName
						break
					case 'percentComplete':
						columnMappings.percentCompleteColumn = columnName
						break
					default:
						columnMappings[colType] = columnName
				}
			}
		})

		// aka 'metricColumns' in AbstractTabularImportForm
		columnMappings.nameColumns = _(nameColumns).sortBy('order').map('name').value()

		data.columnMappings = columnMappings

		this.model.set(data)
	},

	test: function() {
		let problem = FormView.prototype.test.apply(this, arguments)
		// #31642 display message if database is offline/unavailable
		if (!problem) {
			problem = this.getDataSourceMetadataMessage()
		}
		if (!problem && this.sourceDataPanel) {
			problem = this.sourceDataPanel.test()
		}

		if (problem) {
			return problem
		}

		// date is required if 'use specific date" is selected
		if (this.dateReference() === 'specific' && _.isEmpty(this.dateInput.val())) {
			return (i18n.get('admin.import.useSpecificDate.pleaseChoose'))
		}
	},

	/**
	 * Returns an array of valid column mapping options for the current settings.
	 */
	validMappingOptions () {
		let dateReference = this.dateReference()

		if (this.model.get('type') === 'initiative-status') {
			if (dateReference === 'column') {
				// if the date is in a column, all the possible mappings are valid
				return INITIATIVE_MAPPING_OPTIONS
			} else {
				// if the date is not in a column ('relative' or 'specific'), then
				// all the mapping options except for 'date' are valid
				return INITIATIVE_MAPPING_OPTIONS.filter(o => o.id !== 'date')
			}
		} else {
			if (dateReference === 'row') {
				// if we're using a date header row, only metric id/name and
				// note should be mappable. (#34996)
				return DATE_HEADER_MAPPING_OPTIONS
			}

			if (dateReference === 'column') {
				// if the date is in a column, all the possible mappings are valid
				return METRIC_MAPPING_OPTIONS
			}

			// if the date is not in a column ('relative' or 'specific'), then
			// all the mapping options except for 'date' are valid
			return METRIC_MAPPING_OPTIONS.filter(o => o.id !== 'date')
		}
	},

	/**
	 * Sets 'is-required' class on the required column mappings for the current settings.
	 */
	markRequiredOptions() {
		this.$('.porter-package').removeClass('is-required')
		// for all date reference types, the first id/name mapping is required
		this.$('.porter-package[data-id=name][data-order=1]').toggleClass('is-required', true)
		// for dates in a column, the date mapping is also required
		let dateRequired = this.dateReference() === 'column'
		this.$('.porter-package[data-id=date]').toggleClass('is-required', dateRequired)
		// for column, relative, or specific date,
		// at least one of the following is also required: value, threshold, or note
		// but will be validated on the server
	},

	/**
	 * Returns the value from the "date reference" dropdown ('specific', 'column', etc)
	 */
	dateReference () {
		return this.$('[name=dateReference]').val()
	},

	submit: function() {
		this.saveState()

		return $.post({
			data: JSON.stringify(this.model),
			url: 'api/imports/validate-source-data',
			success: (response) => {
				this.model.set(response)
				this.options.wizard.next()
			},
			error: (xhr) => {
				let errors = xhr.responseJSON ? xhr.responseJSON.errors : false // validation errors
				if (errors) {
					let messages = _.map(errors, 'message')
					this.formView.errorMessages(messages)
				} else { // other types of errors
					this.formView.error(xhr)
				}
			}
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.dateInput, this.sourceDataPanel,
			this.markedField], 'remove')
	}
})

const DATE_HEADER_MAPPING_OPTIONS = [
	{ id: 'name', label: 'admin.import.metricIdName', multiple: true },
	{ id: 'note', label: 'common.note.note' },
	{ id: 'orgLabel', label: 'common.organization.organization'}
]

const METRIC_MAPPING_OPTIONS = [
	{ id: 'name', label: 'admin.import.metricIdName', multiple: true },
	{ id: 'date', label: 'common.date' },
	{ id: 'value', label: 'common.value', multiple: true },
	{ id: 'threshold', label: 'scorecards.node.metric.threshold', multiple: true },
	{ id: 'note', label: 'common.note.note' },
	{ id: 'orgLabel', label: 'common.organization.organization'}
]

const INITIATIVE_MAPPING_OPTIONS = [
	{ id: 'name', label: 'admin.import.initiativeIdName', multiple: true },
	{ id: 'date', label: 'common.date' },
	{ id: 'note', label: 'common.note.note' },
	{ id: 'budgetSpent', label: 'initiatives.budgetSpentToDate'},
	{ id: 'percentComplete', label: 'initiatives.percentComplete'}
]

export default ViSourceData

ViSourceData.__filename__ = 'imports/vi/vi-source-data.js'
